<template>
  <Page class="skill-page" :pagination="`${page}/${total_pages}`">
    <template #content>
      <span
        class="page-title"
        v-text="modules[moduleIndex].name.toUpperCase()"
      />
      <div class="header-note">
        <ProgressCircular
          v-if="modules[moduleIndex].type !== 'style'"
          :value="result.score"
          :data-convert="`${result.module}_circular`"
          :label="result.rank"
          :rank="result.rank"
        />
        <Tag
          style="margin: 12px auto 24px"
          class="yellow bold"
          :text="modules[moduleIndex].result_wordings[result.rank].label"
          v-if="modules[moduleIndex].type === 'style'"
          :data-convert="`${result.module}_circular`"
        />
        <p
          class="appreciation bold"
          v-text="
            appreciation ??
            modules[moduleIndex].result_wordings[result.rank].analysis
          "
        />
      </div>
      <div
          v-if="
            social_desire_score?.score > 75 &&
            [
              'empathy_score',
              'active_listen_score',
              'auto_efficient_score',
            ].includes(result.module)
          "
          v-tooltip="
            'Les questionnaires indiquent une forte tendance à la désirabilité sociale chez ce talent. Cette inclinaison peut refléter une volonté de mettre en avant une image positive de soi, ce qui peut influencer certaines réponses. Afin d’obtenir une évaluation plus complète et nuancée de ces compétences, il peut être pertinent de croiser ces résultats avec d’autres méthodes d’évaluation.'
          "
        >
          <i class="bi-info-circle" style="color: #f2cb36" />
          <span style="margin-left: 8px; font-size: 14px"
            >Tendance à la désirabilité sociale détectée sur cette
            compétence</span
          >
        </div>
      <div class="content-wrapper">
        <div class="content-block">
          <div class="content-title">Notre analyse :</div>
          <div
            class="content"
            v-text="modules[moduleIndex].result_wordings[result.rank].analysis"
          />
        </div>
        <div class="content-block">
          <div class="content-title">Nos conseils :</div>
          <div
            class="content"
            v-text="modules[moduleIndex].result_wordings[result.rank].advices"
          />
        </div>
      </div>
      <div
        v-if="currentCampaign.version === 1"
        class="badge-wrapper"
        :class="`position-${candidate_position}`"
        :data-convert="`${result.module}_badge`"
      >
        <div class="badge-inner">
          <Badge
            :position="candidate_position"
            :module="result.module"
            :use_gradients="false"
            data-html2canvas-ignore
          />
        </div>

        <div class="badge-content" data-html2canvas-ignore>
          <p class="bold">Compétence N°{{ candidate_position }} :</p>
          <p>
            Pour cette compétence, voici le badge obtenu par ce profil sur son
            rapport de participant. Vous pouvez le mentionner lors de vos
            échanges.
          </p>
        </div>
        <div class="badge-tag">
          <div class="badge-tag-hidden">Badge {{ position_label }}</div>
          <div class="badge-tag-shown" data-html2canvas-ignore>
            Badge {{ position_label }}
          </div>
        </div>
      </div>
    </template>
  </Page>
  <Page :pagination="`${page + 1}/${total_pages}`">
    <template #content>
      <span
        class="page-title"
        v-text="modules[moduleIndex].name.toUpperCase()"
      />
      <div class="content-wrapper">
        <div class="content-block">
          <div class="content-title">Définition :</div>
          <div
            class="content"
            v-html="modules[moduleIndex].result_wordings.skill_description"
          />
        </div>
        <div class="content-block">
          <div class="content-title">Son importance :</div>
          <div class="content">
            <div
              v-for="advantage in modules[moduleIndex].result_wordings
                .advantages"
              :key="advantage"
              class="avantage"
            >
              <div class="icon-container">
                <i class="yuzu-icon" :class="advantage.icon" />
              </div>
              <p v-html="advantage.text" />
            </div>
          </div>
        </div>
        <div class="content-block">
          <div class="content-title">
            3 questions pour approfondir cette évaluation :
          </div>
          <div class="content">
            <ol>
              <li
                v-for="(question, index) in modules[moduleIndex]
                  .result_wordings[result.rank].questions"
                :key="index"
                v-text="question"
              />
            </ol>
            <p
              v-if="currentCampaign.version === 1"
              v-html="modules[moduleIndex].result_wordings.questions_info"
            />
          </div>
        </div>
      </div>
    </template>
  </Page>
</template>

<script setup>
import { computed } from "vue";
import { useModulesStore } from "@/stores/modules.js";
import { useCampaignStore } from "@/stores/campaign.js";
import Page from "../Page.vue";
import Badge from "@/components/game/badges/Badge.vue";

const { modules } = useModulesStore();

const { currentCampaign } = useCampaignStore();

const props = defineProps({
  result: {
    type: Object,
    required: true,
  },
  appreciation: {
    type: String,
  },
  index: {
    type: Number,
    required: true,
  },
  total_pages: {
    type: Number,
    required: true,
  },
  social_desire_score: {
    type: Object,
  },
});

const page = computed(() => {
  return props.index * 2 + 3;
});

const moduleIndex = computed(() => {
  if (currentCampaign.version === 1) {
    return modules.findIndex((elt) => elt.id === props.result.module);
  }

  return modules.findIndex(
    (elt) =>
      props.result.module.includes(elt.id_v2) ||
      props.result.module.includes(elt.id)
  );
});

const candidate_position = computed(() => {
  return props.result.badge_rank;
});

const position_label = computed(() => {
  const labels = ["Or", "Argent", "Bronze"];

  return labels[candidate_position.value - 1];
});
</script>
